import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * ThemeContext — global light/dark/system theme control.
 *
 *   mode:        "light" | "dark" | "system"
 *   resolved:    "light" | "dark"         (what is actually applied)
 *   setMode(m):  switch mode
 *   toggle():    cycle dark <-> light (skips system)
 *
 * Persists to localStorage under "prosale:theme".
 * Applies the `dark` class to <html> so Tailwind's `darkMode: "class"`
 * strategy picks it up everywhere.
 *
 * Early "flash of wrong theme" prevention is handled by the inline script
 * below (`THEME_INIT_SNIPPET`) — inject it into `public/index.html` head.
 */

const STORAGE_KEY = "prosale:theme";
const VALID = ["light", "dark", "system"];

const ThemeContext = createContext(null);

function getSystem() {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.includes(v) ? v : "system";
  } catch {
    return "system";
  }
}

function applyResolvedClass(resolved) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.setAttribute("data-theme", resolved);
  // Let the browser chrome (address bar etc.) follow the theme
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute("content", resolved === "dark" ? "#0f172a" : "#ffffff");
}

export function ThemeProvider({ children }) {
  const [mode, setModeState] = useState(readStored);
  const [systemPref, setSystemPref] = useState(getSystem);

  // Respond to OS-level changes while mode === "system"
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setSystemPref(e.matches ? "dark" : "light");
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, []);

  const resolved = mode === "system" ? systemPref : mode;

  // Sync <html>.dark class + persist
  useEffect(() => {
    applyResolvedClass(resolved);
  }, [resolved]);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, mode); } catch { /* noop */ }
  }, [mode]);

  const setMode = useCallback((m) => {
    if (!VALID.includes(m)) return;
    setModeState(m);
  }, []);

  const toggle = useCallback(() => {
    setModeState((m) => {
      const cur = m === "system" ? getSystem() : m;
      return cur === "dark" ? "light" : "dark";
    });
  }, []);

  const value = useMemo(
    () => ({ mode, resolved, setMode, toggle }),
    [mode, resolved, setMode, toggle]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}

/**
 * Drop this <script> into public/index.html inside <head> to set the right
 * class before React boots, eliminating the flash of wrong theme:
 *
 *   <script>${THEME_INIT_SNIPPET}</script>
 */
export const THEME_INIT_SNIPPET = `
(function(){try{
  var k='${STORAGE_KEY}', m=localStorage.getItem(k)||'system';
  var d=(m==='dark')||(m==='system'&&matchMedia('(prefers-color-scheme:dark)').matches);
  document.documentElement.classList.toggle('dark',d);
  document.documentElement.setAttribute('data-theme', d?'dark':'light');
}catch(e){}})();
`.trim();
